import { useState, useMemo } from "react";
import { PronosticV2Service } from "services/pronosticv2.service";
import { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import type { Event } from "event/types/event.model";

interface TestParams {
  L1: string;
  L2: string;
  L3: string;
  L4: string;
  subPlaceId: string;
  bingoGameId: string;
}

interface TestResult {
  allFetchedEvents: Event[];
  todayEvents: Event[];
  validEvents: Event[];
  redirectTarget: string | null;
  redirectType: string;
  error: string | null;
}

const EMPTY_PARAMS: TestParams = {
  L1: "",
  L2: "",
  L3: "",
  L4: "",
  subPlaceId: "",
  bingoGameId: "",
};

function parseParamsFromUrl(raw: string): TestParams | null {
  try {
    const url = new URL(
      raw.includes("://")
        ? raw
        : `https://x.com${raw.startsWith("/") ? "" : "/"}${raw}`,
    );
    return {
      L1: url.searchParams.get("L1") ?? "",
      L2: url.searchParams.get("L2") ?? "",
      L3: url.searchParams.get("L3") ?? "",
      L4: url.searchParams.get("L4") ?? "",
      subPlaceId: url.searchParams.get("subPlaceId") ?? "",
      bingoGameId: url.searchParams.get("bingoGameId") ?? "",
    };
  } catch {
    return null;
  }
}

export function TestRedirectorView() {
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [params, setParams] = useState<TestParams>(EMPTY_PARAMS);
  const [result, setResult] = useState<TestResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const handleParseUrl = () => {
    const parsed = parseParamsFromUrl(urlInput.trim());
    if (!parsed) {
      setUrlError("Could not parse URL");
      return;
    }
    setUrlError(null);
    setParams(parsed);
  };

  const service = useMemo(() => new PronosticV2Service(), []);

  const handleTest = async () => {
    setIsRunning(true);
    setResult(null);

    try {
      const events = (await service.getAllEvents()) as unknown as Event[];
      const today = new Date().toDateString();
      const now = new Date();

      const subPlaceId = params.subPlaceId || null;
      const bingoGameId = params.bingoGameId || null;
      const L1 = params.L1 || null;
      const L2 = params.L2 || null;

      // Handball path
      if (L2?.toLowerCase() === "handball" && !subPlaceId) {
        const todayEvents = events.filter((event) => {
          const eventDate = new Date(event.kickoff_date).toDateString();
          return eventDate === today && event.organization === L1;
        });

        const upcomingEvents = todayEvents.filter(
          (event) => new Date(event.kickoff_date) > now,
        );

        upcomingEvents.sort(
          (a, b) =>
            new Date(a.kickoff_date).getTime() -
            new Date(b.kickoff_date).getTime(),
        );

        const target = upcomingEvents[0] ?? null;

        setResult({
          allFetchedEvents: events,
          todayEvents,
          validEvents: upcomingEvents,
          redirectTarget: target ? `/event/${target.id}` : null,
          redirectType: "handball — event homepage",
          error:
            upcomingEvents.length === 0
              ? "Il n'y a pas d'événement ouvert pour le moment"
              : null,
        });
        return;
      }

      // Validate
      if (!subPlaceId && !bingoGameId) {
        setResult({
          allFetchedEvents: events,
          todayEvents: [],
          validEvents: [],
          redirectTarget: null,
          redirectType: "none",
          error:
            "Vous n'êtes pas localisé correctement, veuillez contacter l'organisateur",
        });
        return;
      }

      const todayEvents = events.filter((event) => {
        const eventDate = new Date(event.kickoff_date).toDateString();
        if (bingoGameId) {
          return (
            eventDate === today &&
            event.organization === L1 &&
            event.bingo_game_display &&
            event.bingo_game_id &&
            event.bingo_game_id === bingoGameId
          );
        } else {
          return (
            eventDate === today &&
            event.organization === L1 &&
            event.pronostics_v2_display &&
            event.pronostics_v2_id
          );
        }
      });

      let validEvents: Event[] = [];
      if (bingoGameId) {
        validEvents = todayEvents;
      } else {
        for (const event of todayEvents) {
          if (event.pronostics_v2_id) {
            try {
              const pronosticV2 = (await service.getPronosticV2ById({
                pronosticV2Id: event.pronostics_v2_id,
              })) as unknown as PronosticV2;
              const subplaces =
                pronosticV2?.commonSettings.general.moduleSettings.place
                  .subPlaces;
              if (subplaces?.find((sp) => sp.id === subPlaceId)) {
                validEvents.push(event);
              }
            } catch {
              // skip event if pronostic fetch fails
            }
          }
        }
      }

      let redirectTarget: string | null = null;
      let redirectType = "none";
      let error: string | null = null;

      if (validEvents.length === 0) {
        error = "Il n'y a pas d'événement ouvert pour le moment";
      } else if (validEvents.length === 1) {
        redirectTarget = `/event/${validEvents[0].id}`;
        redirectType = bingoGameId
          ? `bingogame (bingoGameId: ${bingoGameId})`
          : `pronosticsV2 (subPlaceId: ${subPlaceId})`;
      } else {
        error =
          "Vous n'êtes pas localisé correctement, veuillez contacter l'organisateur";
      }

      setResult({
        allFetchedEvents: events,
        todayEvents,
        validEvents,
        redirectTarget,
        redirectType,
        error,
      });
    } catch (err) {
      console.error(err);
      setResult({
        allFetchedEvents: [],
        todayEvents: [],
        validEvents: [],
        redirectTarget: null,
        redirectType: "none",
        error: "Erreur lors du chargement des événements",
      });
    } finally {
      setIsRunning(false);
    }
  };

  const field = (
    label: string,
    key: keyof TestParams,
    placeholder?: string,
  ) => (
    <div style={{ marginBottom: 8 }}>
      <label style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}>
        {label}
      </label>
      <input
        type="text"
        placeholder={placeholder ?? label}
        value={params[key]}
        onChange={(e) => setParams((p) => ({ ...p, [key]: e.target.value }))}
        style={{
          padding: "6px 10px",
          border: "1px solid #ccc",
          borderRadius: 4,
          width: 260,
          fontSize: 14,
        }}
      />
    </div>
  );

  return (
    <div
      style={{
        fontFamily: "monospace",
        padding: 24,
        maxWidth: 900,
        margin: "0 auto",
      }}
    >
      <h2 style={{ marginBottom: 16 }}>Test Redirector</h2>

      <div style={{ marginBottom: 20 }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}
        >
          Parse from URL
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            placeholder="Paste full QR code URL or ?L1=136&L2=handball&..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            style={{
              padding: "6px 10px",
              border: urlError ? "1px solid #c00" : "1px solid #ccc",
              borderRadius: 4,
              width: 460,
              fontSize: 14,
            }}
          />
          <button
            onClick={handleParseUrl}
            style={{
              padding: "6px 14px",
              backgroundColor: "#2a6496",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Parse
          </button>
          {urlError && (
            <span style={{ color: "#c00", fontSize: 13 }}>{urlError}</span>
          )}
        </div>
        <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
          Fills the fields below automatically.
        </div>
      </div>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          {field("L1 (organization)", "L1", "e.g. 136")}
          {field("L2", "L2", "e.g. handball")}
          {field("L3", "L3")}
          {field("L4", "L4")}
          {field("subPlaceId", "subPlaceId")}
          {field("bingoGameId", "bingoGameId")}

          <button
            onClick={handleTest}
            disabled={isRunning}
            style={{
              marginTop: 8,
              padding: "8px 20px",
              backgroundColor: isRunning ? "#aaa" : "#4a4a9c",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: isRunning ? "not-allowed" : "pointer",
              fontSize: 14,
            }}
          >
            {isRunning ? "Running..." : "Test"}
          </button>
        </div>

        {result && (
          <div style={{ flex: 1, minWidth: 300 }}>
            <Section title="Redirect outcome">
              {result.error ? (
                <Tag color="#8F0C0C">{result.error}</Tag>
              ) : (
                <>
                  <Row label="Target" value={result.redirectTarget ?? "—"} />
                  <Row label="Type" value={result.redirectType} />
                </>
              )}
            </Section>

            <Section title={`Today's events (${result.todayEvents.length})`}>
              {result.todayEvents.length === 0 ? (
                <span style={{ color: "#888" }}>none</span>
              ) : (
                <EventTable
                  events={result.todayEvents}
                  highlighted={result.validEvents}
                />
              )}
            </Section>

            <Section
              title={`Valid / upcoming events (${result.validEvents.length})`}
            >
              {result.validEvents.length === 0 ? (
                <span style={{ color: "#888" }}>none</span>
              ) : (
                <EventTable
                  events={result.validEvents}
                  highlighted={result.validEvents}
                />
              )}
            </Section>

            <Section
              title={`All fetched events (${result.allFetchedEvents.length})`}
            >
              <details>
                <summary style={{ cursor: "pointer", color: "#555" }}>
                  Show all
                </summary>
                <EventTable events={result.allFetchedEvents} highlighted={[]} />
              </details>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontWeight: "bold",
          borderBottom: "1px solid #ccc",
          paddingBottom: 4,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <span style={{ color: "#666" }}>{label}: </span>
      <span>{value}</span>
    </div>
  );
}

function Tag({
  color,
  children,
}: {
  color: string;
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        backgroundColor: color,
        color: "#fff",
        borderRadius: 4,
        padding: "2px 8px",
        fontSize: 13,
      }}
    >
      {children}
    </span>
  );
}

function EventTable({
  events,
  highlighted,
}: {
  events: Event[];
  highlighted: Event[];
}) {
  const highlightedIds = new Set(highlighted.map((e) => e.id));
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 12,
        marginTop: 4,
      }}
    >
      <thead>
        <tr style={{ background: "#eee" }}>
          <th style={th}>ID</th>
          <th style={th}>Name</th>
          <th style={th}>Org</th>
          <th style={th}>Kickoff</th>
          <th style={th}>Flags</th>
        </tr>
      </thead>
      <tbody>
        {events.map((e) => (
          <tr
            key={e.id}
            style={{
              background: highlightedIds.has(e.id) ? "#e8ffe8" : undefined,
            }}
          >
            <td style={td}>{e.id}</td>
            <td style={td}>{e.name ?? "—"}</td>
            <td style={td}>{e.organization}</td>
            <td style={td}>
              {new Date(e.kickoff_date).toLocaleString("fr-FR")}
            </td>
            <td style={td}>
              {e.pronostics_v2_display && <Tag color="#4a4a9c">prono</Tag>}{" "}
              {e.bingo_game_display && <Tag color="#c07a00">bingo</Tag>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const th: React.CSSProperties = {
  padding: "4px 8px",
  textAlign: "left",
  border: "1px solid #ddd",
};
const td: React.CSSProperties = {
  padding: "4px 8px",
  border: "1px solid #ddd",
  verticalAlign: "top",
};
